import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { DatePicker } from "components/designV2/datePicker/datePicker";
import { InputField } from "components/designV2/inputfield/inputfield";
import { Modal } from "components/designV2/modal/modal";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { TimePicker } from "components/designV2/timePicker/timePicker";
import { useAlertStore } from "stores/alertStore/alertStore";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import {
  ObAmountFieldSlot,
  ObAmountRow,
  ObDateFieldSlot,
  ObDayGrid,
  ObDayOption,
  ObFieldMessage,
  ObFormLayout,
  ObFormSection,
  ObHiddenRadio,
  ObModalActions,
  ObRadioControl,
  ObRadioGroup,
  ObRadioLabel,
  ObRadioOption,
  ObSectionButtonSlot,
  ObSectionLabel,
  ObSummaryLabel,
  ObSummaryPanel,
  ObSummaryRow,
  ObSummaryValue,
  ObTimeFieldSlot,
  ObTimeFields,
  ObTimeSeparator,
} from "./obSettings.styled";
import { ChronoUnit, LocalDate, LocalTime } from "@js-joda/core";
import moment, { Moment } from "moment";
import {
  CompanyOBSetting,
  OBSettingWeekDayEN,
  OBSettingWeekDaySE,
} from "model/Company";
import { isObSettingOvernight } from "./obSettings.logic";
import { useObSettings } from "./useObSettings.hook";

type Props = {
  setShowObForm: React.Dispatch<React.SetStateAction<boolean>>;
  showObForm: boolean;
};

const toLocalDateValue = (value: Moment | null): LocalDate | null =>
  value ? LocalDate.parse(value.format("YYYY-MM-DD")) : null;

const getDefaultTimes = (): { startTime: string; endTime: string } => {
  const nowTime = LocalTime.now().truncatedTo(ChronoUnit.MINUTES);
  return {
    startTime: nowTime.plusHours(1).truncatedTo(ChronoUnit.HOURS).toString(),
    endTime: nowTime.plusHours(2).truncatedTo(ChronoUnit.HOURS).toString(),
  };
};

export const ObSettingsForm: React.FC<Props> = ({
  setShowObForm,
  showObForm,
}) => {
  const { t, i18n: translation } = useTranslation();

  const [companyState] = useCompanyStore();
  const [, , alertDispatch] = useAlertStore();
  const { saveObSettings } = useObSettings();

  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [obType, setObType] = useState<"Percent" | "Kr">("Kr");
  const [obValue, setObValue] = useState("10.00");
  const [obValueError, setObValueError] = useState(false);
  const [useDate, setUseDate] = useState(false);
  const [date, setDate] = useState<Moment | null>(moment());
  const today = LocalDate.now();

  const [isLoading, setIsLoading] = useState(false);
  const [weekList, setWeekList] = useState<string[]>([]);
  const [overlapping, setOverlapping] = useState(false);
  const previousTimeRangeRef = useRef<{
    startTime: string;
    endTime: string;
  } | null>(null);

  const options = useMemo(
    () => [
      t("WeekDays.MONDAY"),
      t("WeekDays.TUESDAY"),
      t("WeekDays.WEDNESDAY"),
      t("WeekDays.THURSDAY"),
      t("WeekDays.FRIDAY"),
      t("WeekDays.SATURDAY"),
      t("WeekDays.SUNDAY"),
    ],
    [t],
  );

  const showObError = useCallback((): void => {
    alertDispatch({
      type: "SHOW_ALERT",
      payload: {
        icon: "alert",
        title: t("Alerts.OhNo"),
        message: t("General.ErrorOccurred"),
      },
    });
  }, [alertDispatch, t]);

  const resetForm = (): void => {
    const defaultTimes = getDefaultTimes();
    setWeekList([]);
    setDate(moment());
    setStartTime(defaultTimes.startTime);
    setEndTime(defaultTimes.endTime);
    setObType("Kr");
    setUseDate(false);
  };

  useEffect(() => {
    const defaultTimes = getDefaultTimes();
    setStartTime(defaultTimes.startTime);
    setEndTime(defaultTimes.endTime);
  }, []);

  const formatDayLabel = (value: string): string => {
    const normalizedValue = value.toLocaleLowerCase(translation.language);
    return `${normalizedValue
      .charAt(0)
      .toLocaleUpperCase(translation.language)}${normalizedValue.slice(1)}`;
  };

  const isFullDaySelected = startTime === "00:00" && endTime === "00:00";

  const toggleFullDay = (): void => {
    if (isFullDaySelected) {
      const restoredTimes = previousTimeRangeRef.current ?? getDefaultTimes();
      setStartTime(restoredTimes.startTime);
      setEndTime(restoredTimes.endTime);
      previousTimeRangeRef.current = null;
      return;
    }

    previousTimeRangeRef.current = { startTime, endTime };
    setStartTime("00:00");
    setEndTime("00:00");
  };

  const getTotalObTime = (): string | undefined => {
    if (startTime && endTime) {
      if (startTime === endTime) {
        return "24h 0min";
      } else {
        let minDur = moment
          .duration(
            moment(
              isObSettingOvernight({ endTime, startTime }) ||
                endTime === "00:00"
                ? "2022-03-04T" + endTime
                : "2022-03-03T" + endTime,
            ).diff(moment("2022-03-03T" + startTime)),
          )
          .asMinutes();
        const HDur = Math.floor(minDur / 60);
        return `${HDur}${t("General.HourShortened")} ${minDur % 60}min`;
      }
    }
  };

  const checkIfOverlappingSettings = useCallback(() => {
    if (weekList.length > 0 && !useDate) {
      let overlapp = 0;
      weekList.forEach((day) => {
        const dayEnum = options.indexOf(day) + 1;
        const sameDaySettings = companyState.obSettings.filter(
          (setting) => setting.weekday === dayEnum,
        );
        sameDaySettings.forEach((setting: CompanyOBSetting) => {
          const settingStartTime = moment(setting.startTime, "HH:mm:ss");
          const settingEndTime = moment(setting.endTime, "HH:mm:ss");
          const settingStartDate = moment("2022-12-10");

          settingStartDate.set({
            hour: settingStartTime.get("hour"),
            minute: settingStartTime.get("minute"),
            second: settingStartTime.get("second"),
          });
          const settingEndDate = moment("2022-12-10");

          settingEndDate.set({
            hour: settingEndTime.get("hour"),
            minute: settingEndTime.get("minute"),
            second: settingEndTime.get("second"),
          });

          if (settingEndDate < settingStartDate) {
            settingEndDate.add(1, "days");
          }

          const newStartTime = moment(startTime, "HH:mm:ss");
          const newEndTime = moment(endTime, "HH:mm:ss");
          const newStartDate = moment("2022-12-10");

          newStartDate.set({
            hour: newStartTime.get("hour"),
            minute: newStartTime.get("minute"),
            second: newStartTime.get("second"),
          });

          const newEndDate = moment("2022-12-10");

          newEndDate.set({
            hour: newEndTime.get("hour"),
            minute: newEndTime.get("minute"),
            second: newEndTime.get("second"),
          });
          if (newEndDate < newStartDate) {
            newEndDate.add(1, "days");
          }

          if (
            newStartDate <= settingEndDate &&
            settingStartDate <= newEndDate
          ) {
            overlapp++;
          }
        });
        if (overlapp > 0) {
          setOverlapping(true);
        } else {
          setOverlapping(false);
        }
      });
    } else if (date && useDate) {
      let overlapp = 0;
      const sameDaySettings = companyState.obSettings.filter(
        (setting) => setting.date === date.format("YYYY-MM-DD"),
      );
      sameDaySettings.forEach((setting: CompanyOBSetting) => {
        const settingStartTime = moment(setting.startTime, "HH:mm:ss");
        const settingEndTime = moment(setting.endTime, "HH:mm:ss");
        const settingStartDate = moment("2022-12-10");

        settingStartDate.set({
          hour: settingStartTime.get("hour"),
          minute: settingStartTime.get("minute"),
          second: settingStartTime.get("second"),
        });
        const settingEndDate = moment("2022-12-10");

        settingEndDate.set({
          hour: settingEndTime.get("hour"),
          minute: settingEndTime.get("minute"),
          second: settingEndTime.get("second"),
        });

        if (settingEndDate < settingStartDate) {
          settingEndDate.add(1, "days");
        }

        const newStartTime = moment(startTime, "HH:mm:ss");
        const newEndTime = moment(endTime, "HH:mm:ss");
        const newStartDate = moment("2022-12-10");

        newStartDate.set({
          hour: newStartTime.get("hour"),
          minute: newStartTime.get("minute"),
          second: newStartTime.get("second"),
        });

        const newEndDate = moment("2022-12-10");

        newEndDate.set({
          hour: newEndTime.get("hour"),
          minute: newEndTime.get("minute"),
          second: newEndTime.get("second"),
        });
        if (newEndDate < newStartDate) {
          newEndDate.add(1, "days");
        }

        if (newStartDate <= settingEndDate && settingStartDate <= newEndDate) {
          overlapp++;
        }
      });
      if (overlapp > 0) {
        setOverlapping(true);
      } else {
        setOverlapping(false);
      }
    } else {
      setOverlapping(false);
    }
  }, [
    companyState.obSettings,
    date,
    endTime,
    options,
    startTime,
    useDate,
    weekList,
  ]);

  useEffect(() => {
    checkIfOverlappingSettings();
  }, [checkIfOverlappingSettings, showObForm]);

  const validateDate = (): boolean =>
    Boolean(date && today.toString() <= date.format("YYYY-MM-DD"));

  const validateStartTime = (): boolean => {
    if (useDate && date && today.toString() === date.format("YYYY-MM-DD")) {
      return (
        startTime >= LocalTime.now().truncatedTo(ChronoUnit.MINUTES).toString()
      );
    } else {
      return true;
    }
  };

  const parsedObValue = Number.parseFloat(obValue);
  const isObValueValid =
    !obValueError && Number.isFinite(parsedObValue) && parsedObValue > 0;

  const validateForm = (): boolean =>
    Boolean(
      startTime &&
      endTime &&
      validateStartTime() &&
      isObValueValid &&
      !overlapping &&
      ((useDate && date) || (!useDate && weekList.length > 0)),
    );

  const getWeekdayIndex = useCallback(
    (dayString: string): number | null => {
      const lookup =
        translation.language === "sv" ||
        translation.language === "sv-se" ||
        translation.language === "se"
          ? OBSettingWeekDaySE
          : OBSettingWeekDayEN;

      const dayIndex = lookup[dayString as keyof typeof lookup];
      return typeof dayIndex === "number" ? dayIndex : null;
    },
    [translation.language],
  );

  const handleSubmit = async (): Promise<void> => {
    if (isLoading || !validateForm()) {
      return;
    }

    setIsLoading(true);

    const dayIndexes = weekList
      .map(getWeekdayIndex)
      .filter((dayIndex): dayIndex is number => dayIndex !== null);
    const status = await saveObSettings({
      dateString: useDate && date ? date.format("YYYY-MM-DD") : null,
      dayIndexes,
      endTime,
      obType,
      obValue: parsedObValue,
      startTime,
      useDate,
    });

    if (status === "success") {
      resetForm();
      setShowObForm(false);
    } else {
      showObError();
    }

    setIsLoading(false);
  };

  const summaryDays = useDate
    ? (date?.format("YYYY-MM-DD") ?? "")
    : weekList.map(formatDayLabel).join(", ");
  const timeSummary =
    startTime && endTime
      ? `${startTime} - ${endTime}${
          getTotalObTime() ? ` (${getTotalObTime()})` : ""
        }`
      : "";
  const compensationSummary =
    parseFloat(obValue) > 0
      ? obType === "Percent"
        ? `${obValue}%`
        : `${obValue}${t("OrganizationalStructure.salaryPerHour")}`
      : "";
  const hasDateError = Boolean(useDate && date && !validateDate());
  const hasTimeError = !validateStartTime();
  const requiredSuffix = `(${t("System.Required").toLocaleLowerCase(
    translation.language,
  )})`;
  const compensationLabel =
    obType === "Percent"
      ? `${t("ProfileCompany.Compensation")} (%) ${requiredSuffix}`
      : `${t("ProfileCompany.Compensation")} (${t("ProfileCompany.Kr")}) ${requiredSuffix}`;

  return (
    <Modal
      label={t("ProfileCompany.OBInconvenientWorkingHours")}
      isOpen={showObForm}
      onClose={() => {
        setShowObForm(false);
      }}
      size="small"
      footer={
        <ObModalActions>
          <SecondaryButton
            label={t("General.Cancel")}
            disabled={isLoading}
            onClick={() => setShowObForm(false)}
          />
          <PrimaryButton
            label={t("General.Save")}
            disabled={!validateForm()}
            isLoading={isLoading}
            onClick={handleSubmit}
          />
        </ObModalActions>
      }
    >
      <ObFormLayout>
        <ObFormSection>
          <ObSectionLabel>
            {useDate ? t("ProfileCompany.Date") : t("ProfileCompany.Weekdays")}
          </ObSectionLabel>

          {!useDate ? (
            <ObDayGrid>
              {options.map((day) => {
                const selected = weekList.some((weekday) => weekday === day);
                return (
                  <ObDayOption
                    key={day}
                    type="button"
                    $selected={selected}
                    onClick={() => {
                      if (!selected) {
                        setWeekList([...weekList, day]);
                      } else {
                        setWeekList(weekList.filter((d) => d !== day));
                      }
                    }}
                  >
                    {formatDayLabel(day)}
                  </ObDayOption>
                );
              })}
            </ObDayGrid>
          ) : (
            <ObDateFieldSlot>
              <DatePicker
                label={t("GigsCompany.Date")}
                value={toLocalDateValue(date)}
                onChange={(nextDate) =>
                  setDate(
                    nextDate ? moment(nextDate.toString(), "YYYY-MM-DD") : null,
                  )
                }
                dateFormat="DD/MM/YYYY"
                earliestSelectableDate={today}
                hasError={hasDateError}
                errorText={t("GigsCompany.DateHasPassed")}
              />
            </ObDateFieldSlot>
          )}

          <ObSectionButtonSlot>
            <SecondaryButton
              label={
                useDate
                  ? t("ProfileCompany.SelectWeekdays")
                  : t("DatePicker.Placeholder")
              }
              size="small"
              onClick={() => setUseDate(!useDate)}
            />
          </ObSectionButtonSlot>
        </ObFormSection>

        <ObFormSection>
          <ObSectionLabel>{t("ProfileCompany.Time")}</ObSectionLabel>

          <ObTimeFields>
            <ObTimeFieldSlot>
              <TimePicker
                label={`${t("ProfileCompany.StartTime")} ${requiredSuffix}`}
                ariaLabel={t("ProfileCompany.StartTime")}
                value={startTime}
                size="compact"
                disabled={isFullDaySelected}
                hasError={hasTimeError}
                onChange={setStartTime}
              />
            </ObTimeFieldSlot>
            <ObTimeSeparator aria-hidden="true">-</ObTimeSeparator>
            <ObTimeFieldSlot>
              <TimePicker
                label={`${t("ProfileCompany.EndTime")} ${requiredSuffix}`}
                ariaLabel={t("ProfileCompany.EndTime")}
                value={endTime}
                size="compact"
                disabled={isFullDaySelected}
                hasError={hasTimeError}
                onChange={setEndTime}
              />
            </ObTimeFieldSlot>
          </ObTimeFields>

          <ObRadioGroup
            role="group"
            aria-label={t("GigsCompany.ToSelectTheEntireDay")}
          >
            <ObRadioOption>
              <ObHiddenRadio
                type="checkbox"
                name="ob-full-day"
                checked={isFullDaySelected}
                onChange={toggleFullDay}
              />
              <ObRadioControl />
              <ObRadioLabel>
                {t("GigsCompany.ToSelectTheEntireDay")}
              </ObRadioLabel>
            </ObRadioOption>
          </ObRadioGroup>

          {hasTimeError && (
            <ObFieldMessage $error>
              {t("GigsCompany.EnterAValidTime")}
            </ObFieldMessage>
          )}
        </ObFormSection>

        <ObFormSection>
          <ObSectionLabel>{t("ProfileCompany.Compensation")}</ObSectionLabel>

          <ObRadioGroup
            role="radiogroup"
            aria-label={t("ProfileCompany.CompensationType")}
          >
            <ObRadioOption>
              <ObHiddenRadio
                type="radio"
                name="ob-compensation-type"
                value="Kr"
                checked={obType === "Kr"}
                onChange={() => setObType("Kr")}
              />
              <ObRadioControl />
              <ObRadioLabel>{t("ProfileCompany.Kr")}</ObRadioLabel>
            </ObRadioOption>

            <ObRadioOption>
              <ObHiddenRadio
                type="radio"
                name="ob-compensation-type"
                value="Percent"
                checked={obType === "Percent"}
                onChange={() => setObType("Percent")}
              />
              <ObRadioControl />
              <ObRadioLabel>{t("ProfileCompany.Percent")}</ObRadioLabel>
            </ObRadioOption>
          </ObRadioGroup>

          <ObAmountRow>
            <ObAmountFieldSlot>
              <InputField
                label={compensationLabel}
                ariaLabel={compensationLabel}
                value={obValue.replace(",", ".")}
                type="number"
                fullWidth
                hasError={obValueError}
                onChange={(value) => {
                  const normalizedValue = value.replace(",", ".");
                  setObValue(normalizedValue);
                  const regex = /^[0-9]+([.,][0-9]{0,3})?$/;
                  setObValueError(!regex.test(value));
                }}
              />
            </ObAmountFieldSlot>
          </ObAmountRow>

          {overlapping && (
            <ObFieldMessage $error>
              {t("ProfileCompany.ThisObSettingIsOverlapping")}
            </ObFieldMessage>
          )}
        </ObFormSection>

        <ObFormSection>
          <ObSummaryPanel>
            <ObSectionLabel>{t("ProfileCompany.Preview")}</ObSectionLabel>

            <ObSummaryRow>
              <ObSummaryLabel>
                {useDate
                  ? t("ProfileCompany.Date")
                  : t("ProfileCompany.Weekdays")}
              </ObSummaryLabel>
              <ObSummaryValue>{summaryDays}</ObSummaryValue>
            </ObSummaryRow>
            <ObSummaryRow>
              <ObSummaryLabel>{t("ProfileCompany.Time")}</ObSummaryLabel>
              <ObSummaryValue>{timeSummary}</ObSummaryValue>
            </ObSummaryRow>
            <ObSummaryRow>
              <ObSummaryLabel>
                {t("ProfileCompany.Compensation")}
              </ObSummaryLabel>
              <ObSummaryValue>{compensationSummary}</ObSummaryValue>
            </ObSummaryRow>
          </ObSummaryPanel>
        </ObFormSection>
      </ObFormLayout>
    </Modal>
  );
};
